import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { ReactComponent as CloseIcon } from '@repo/common/assets/icons/20px/icon_20px_cancel.svg';
import { getOpsPeerReview } from '../../../api/twoDays/ops.api';
import { cn, initials } from '../model/derive';
import { KST, formatDue } from '../model/time';
import { BEHAVIORS, SCALE, STRENGTHS } from '../model/completeProject';
import { StatusChip } from '../primitives';
import useMedia, { MOBILE } from '../useMedia';
import useScrollLock from '../useScrollLock';
import usePresence from '../usePresence';
import { jobLabel } from './opsModel';

/**
 * Ops › Reports › 피어리뷰 응답 보기 (Ops 전용 · 읽기 전용).
 * 오른쪽 시트 520px (모바일은 높이 88% 바텀시트). 스크림 · 닫기 버튼 · Escape 로 닫힌다.
 * 탭: Given(이 사람이 쓴 것) · Received(팀원이 이 사람에 대해 쓴 것). 카드는 제출한 답 그대로.
 * 이탈자 · 스태프는 서버가 빼고 보낸다.
 */

const at = (iso) => `${formatDue(iso, KST)} KST`;

/** 제출 상태 칩 + 한 줄 (on_time · late · open · closed · not_open) */
const stateOf = (state, submittedAt, closesAt) => {
  if (state === 'on_time')
    return { chip: { label: 'Submitted on time', tone: 'success' }, text: `Submitted ${at(submittedAt)}` };
  if (state === 'late')
    return {
      chip: { label: 'Submitted late', tone: 'warning' },
      text: `Submitted ${at(submittedAt)} · after the 3-hour window`,
    };
  if (state === 'open')
    return {
      chip: { label: 'Not submitted yet', tone: 'green' },
      text: closesAt ? `Open until ${at(closesAt)}` : 'Peer review is open',
    };
  if (state === 'closed')
    return {
      chip: { label: 'Not submitted', tone: 'gray' },
      text: closesAt ? `Closed ${at(closesAt)} without a submission` : 'Closed without a submission',
    };
  return { chip: { label: 'Not open yet', tone: 'gray' }, text: 'Peer review hasn’t opened yet' };
};

const scoreLabel = (v) => (v === 'na' ? 'Didn’t see this' : (SCALE[v] ?? '—'));
const strengthLabel = (k) => STRENGTHS.find((s) => s.key === k)?.label ?? '—';

const Leader = () => (
  <span className='inline-flex h-5 items-center rounded-md bg-surface-green10 px-[7px] text-[11px] font-medium text-green-deep04'>
    Leader
  </span>
);

const Label = ({ children }) => (
  <span className='text-[12px] font-medium leading-[140%] text-text-secondary'>{children}</span>
);

/** 챕터 카드 — 머리(To/From · 이름 · 직무) + 제출한 답 */
const ChapterCard = ({ c, dir }) => {
  const st = stateOf(c.state, c.submittedAt, null);
  const a = c.chapter;
  const received = dir === 'received';
  return (
    <section className='flex shrink-0 flex-col rounded-xl border border-green-deep03/[0.12] bg-grayscale-gray00'>
      <div className='flex items-center gap-2.5 px-4 py-3'>
        <span className='flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-grayscale-gray01 text-[12px] font-medium text-text-secondary shadow-[inset_0_0_0_1px_#d5d2cd]'>
          {initials(c.name)}
        </span>
        <span className='flex min-w-0 flex-1 flex-col gap-0.5'>
          <span className='flex flex-wrap items-center gap-1.5'>
            <span className='text-[13px] leading-[140%] text-text-secondary'>
              {received ? 'From' : 'To'}
            </span>
            <span className='text-[14px] font-medium leading-[140%] text-text-primary'>{c.name}</span>
            {c.leader && <Leader />}
          </span>
          <span className='text-[12px] leading-[140%] tabular-nums text-text-secondary'>
            {jobLabel(c.job)}
            {received ? ` · ${st.text}` : ''}
          </span>
        </span>
        {received && <StatusChip chip={st.chip} className='shrink-0' />}
      </div>
      {a && (
        <div className='flex flex-col gap-3.5 border-t border-green-deep03/[0.08] px-4 pb-4 pt-1'>
          <div className='flex flex-col'>
            {BEHAVIORS.map((b, i) => (
              <div
                key={b.key}
                className={cn(
                  'grid grid-cols-[minmax(0,1fr)_auto] items-baseline gap-3 py-2',
                  i > 0 && 'border-t border-green-deep03/[0.08]',
                )}
              >
                <span className='text-[13px] leading-[140%] text-text-secondary [text-wrap:pretty]'>
                  {b.label}
                </span>
                <span className='whitespace-nowrap text-[13px] font-medium leading-[140%] text-text-primary'>
                  {scoreLabel(a.scores?.[b.key])}
                </span>
              </div>
            ))}
          </div>
          <div className='flex flex-col gap-1.5'>
            <Label>Top strength</Label>
            <span className='inline-flex h-[26px] items-center self-start rounded-full bg-surface-green10 px-2.5 text-[13px] font-medium text-green-deep04'>
              {strengthLabel(a.strength)}
            </span>
          </div>
          <div className='flex flex-col gap-1'>
            <Label>What went well</Label>
            <p className='whitespace-pre-line text-[14px] leading-[150%] text-text-primary'>
              {a.praise || '—'}
            </p>
          </div>
          <div className='flex flex-col gap-1'>
            <Label>What to improve</Label>
            <p className='whitespace-pre-line text-[14px] leading-[150%] text-text-primary'>
              {a.improve || '—'}
            </p>
          </div>
          {a.thanks && (
            <div className='flex flex-col gap-1'>
              <Label>Thanks · optional</Label>
              <p className='whitespace-pre-line text-[14px] leading-[150%] text-text-primary'>
                {a.thanks}
              </p>
            </div>
          )}
          <div className='flex items-center justify-between gap-3'>
            <span className='text-[13px] leading-[140%] text-text-secondary'>
              Would work together again
            </span>
            <span className='text-[13px] font-medium leading-[140%] text-text-primary'>
              {a.workAgain === 'yes' ? 'Yes' : a.workAgain === 'no' ? 'No' : '—'}
            </span>
          </div>
        </div>
      )}
    </section>
  );
};

const PeerReviewSheet = ({ row, onClose }) => {
  const open = !!row;
  const { mounted, shown } = usePresence(open, 280);
  const mobile = useMedia(MOBILE);
  useScrollLock(open);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('given');
  const memberId = row?.memberId;

  useEffect(() => {
    if (memberId == null) return undefined;
    let alive = true;
    setData(null);
    setError('');
    setTab('given');
    getOpsPeerReview(memberId)
      .then((d) => alive && setData(d))
      .catch(() => alive && setError('Couldn’t load the responses. Try again.'));
    return () => {
      alive = false;
    };
  }, [memberId]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!mounted) return null;
  const name = data?.name ?? row?.name ?? '';
  const first = name.split(' ')[0];
  const own = data ? stateOf(data.state, data.submittedAt, data.closesAt) : null;
  const mates = data?.received?.length ?? 0;
  const given = data?.given ?? [];
  const receivedIn = (data?.received ?? []).filter((c) => c.chapter).length;
  const list = tab === 'given' ? given : (data?.received ?? []);
  const givenEmpty = data && tab === 'given' && !given.length;

  return ReactDOM.createPortal(
    <div
      className={cn(
        'fixed inset-0 z-[1200] flex bg-[rgba(0,0,0,0.4)] transition-opacity duration-300',
        mobile ? 'items-end' : 'justify-end',
        shown ? 'opacity-100' : 'opacity-0',
      )}
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <aside
        role='dialog'
        aria-modal='true'
        aria-label={`Peer review responses · ${name}`}
        className={cn(
          'flex flex-col overflow-hidden bg-grayscale-gray00 shadow-popup-01 transition-transform duration-300 ease-[cubic-bezier(0.2,0.8,0.2,1)]',
          mobile
            ? 'h-[88%] w-full rounded-t-[24px]'
            : 'h-full w-[520px] max-w-full',
          shown ? 'translate-x-0 translate-y-0' : mobile ? 'translate-y-full' : 'translate-x-full',
        )}
      >
        <div className={cn('flex flex-col gap-3.5', mobile ? 'px-5 pt-5' : 'px-6 pt-6')}>
          <div className='flex items-start gap-3'>
            <span className='flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-surface-green10 text-[13px] font-medium text-green-deep04'>
              {initials(name)}
            </span>
            <div className='flex min-w-0 flex-1 flex-col gap-0.5'>
              <span className='text-[12px] font-medium leading-[140%] text-text-secondary'>
                Peer review responses · read-only
              </span>
              <span className='flex flex-wrap items-center gap-1.5'>
                <h2 className='text-[18px] font-medium leading-[140%] text-text-primary'>{name}</h2>
                {(data?.leader ?? row?.leader) && <Leader />}
              </span>
              <span className='text-[13px] leading-[140%] text-text-secondary'>
                {jobLabel(data?.job ?? row?.job)} · {data?.teamName ?? row?.teamName}
              </span>
            </div>
            <button
              type='button'
              aria-label='Close'
              onClick={onClose}
              className='-mr-2 -mt-2 flex h-11 w-11 shrink-0 items-center justify-center rounded-full hover:bg-green-deep03/[0.08]'
            >
              <CloseIcon aria-hidden className='h-5 w-5 text-icon-secondary' />
            </button>
          </div>
          {own && (
            <div className='flex flex-wrap items-center gap-2'>
              <StatusChip chip={own.chip} />
              <span className='text-[13px] leading-[140%] tabular-nums text-text-secondary'>
                {own.text}
              </span>
            </div>
          )}
          {data && (
            <div
              role='tablist'
              aria-label='Peer review'
              className='mb-1 flex gap-0.5 self-start rounded-full bg-green-deep03/[0.06] p-1'
            >
              {[
                ['given', `Given · ${given.length} of ${mates}`],
                ['received', `Received · ${receivedIn} of ${mates}`],
              ].map(([k, label]) => (
                <button
                  key={k}
                  type='button'
                  role='tab'
                  aria-selected={tab === k}
                  onClick={() => setTab(k)}
                  className={cn(
                    'flex items-center whitespace-nowrap rounded-full px-3.5 text-[14px] transition-colors',
                    mobile ? 'h-10' : 'h-[34px]',
                    tab === k
                      ? 'bg-grayscale-gray00 font-medium text-text-primary shadow-4'
                      : 'text-text-tertiary hover:text-text-primary',
                  )}
                >
                  {label}
                </button>
              ))}
            </div>
          )}
        </div>
        <div
          className={cn(
            'flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto border-t border-green-deep03/[0.08] pt-3',
            mobile ? 'px-5 pb-5' : 'px-6 pb-6',
          )}
        >
          {error ? (
            <p className='py-10 text-center text-[14px] text-functional-failed04'>{error}</p>
          ) : !data ? (
            // 불러오는 중 — 카드 자리만
            <div className='flex flex-col gap-3' aria-busy='true'>
              {[0, 1].map((i) => (
                <div key={i} className='h-40 animate-pulse rounded-xl bg-grayscale-gray01' />
              ))}
            </div>
          ) : (
            <>
              <p className='text-[13px] leading-[150%] text-text-secondary [text-wrap:pretty]'>
                {tab === 'given'
                  ? `What ${first} wrote about each teammate.`
                  : `What each teammate wrote about ${first}. ${first}’s report only shows an AI summary, with no names.`}
              </p>
              {givenEmpty && (
                <div className='flex flex-col items-center gap-2 rounded-xl border border-dashed border-green-deep03/[0.24] px-6 py-10 text-center'>
                  <span className='text-[16px] font-medium leading-[140%] text-text-primary'>
                    {own.chip.label}
                  </span>
                  <span className='text-[14px] leading-[150%] text-text-secondary'>{own.text}</span>
                </div>
              )}
              {list.map((c) => (
                <ChapterCard key={c.memberId} c={c} dir={tab} />
              ))}
            </>
          )}
        </div>
      </aside>
    </div>,
    document.body,
  );
};

export default PeerReviewSheet;
